import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { contractsByUser } from "../Redux/products/slice";
import UpdateContractModal from "./UpdateContract";
import {
  createStyles,
  Paper,
  Grid,
  Text,
  ScrollArea,
  TextInput,
  Button,
  Image,
  Badge,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import Dropdown from "../components/Dropdown";
import { IntlProvider, FormattedMessage, useIntl } from 'react-intl';

const useStyles = createStyles((theme) => ({
  pageBackground: {
    backgroundColor: '#f8f7f3',
    minHeight: '100vh',
    padding: theme.spacing.md,
  },
  contractCard: {
    backgroundColor: theme.white,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    marginBottom: theme.spacing.xl,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows.sm,
    },
    padding: theme.spacing.xl,
  },
  searchFilterCard: {
    backgroundColor: theme.white,
    marginBottom: theme.spacing.xl * 2,
    boxShadow: theme.shadows.xs,
  },
}));

function sortData(data, payload) {
  const { sortBy } = payload;

  let sortedData = [...data];

  if (sortBy) {
    sortedData.sort((a, b) => {
      const valueA = a[sortBy].toString().toLowerCase();
      const valueB = b[sortBy].toString().toLowerCase();

      if (payload.reversed) {
        return valueB.localeCompare(valueA);
      }

      return valueA.localeCompare(valueB);
    });
  }

  return filterData(sortedData, payload.search);
}

function filterData(data, search) {
  const query = search.toLowerCase().trim();
  return data.filter((item) =>
    Object.values(item).some((value) =>
      typeof value === "string" && value.toLowerCase().includes(query)
    )
  );
}

export default function MobileMyContract() {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.products.userContracts);
  const [open, setOpen] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState('');
  const [sortedData, setSortedData] = useState(data || []);
  const [imageUrls, setImageUrls] = useState({});

  const classes = useStyles();
  const intl = useIntl();

  const actions = [
    { label: <FormattedMessage id="action.operate" />, value: 'view' },
    { label: <FormattedMessage id="action.negotiate" />, value: 'update' },
  ];

  useEffect(() => {
    dispatch(contractsByUser());
  }, []);

  useEffect(() => {
    if (data && data.length > 0) {
      setSortedData(data);
    }
  }, [data]);

  const getImages = async (value) => {
    const imageUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/images/${value.productRefID}.png`;
    const defaultImage = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/images/${value.product_name_id}.jpg`; 
    try {
      const response = await fetch(imageUrl);
      if (response.ok) {
        return imageUrl;
      } else {
        return defaultImage;
      }
    } catch (error) {
      return defaultImage;
    }
  };

  useEffect(() => {
    const fetchImages = async () => {
      const urls = await Promise.all(sortedData.map(async (product) => {
        const url = await getImages(product);
        return { productRefID: product.productRefID, url };
      }));
      setImageUrls(urls.reduce((acc, { productRefID, url }) => ({ ...acc, [productRefID]: url }), {}));
    };
  
    fetchImages();
  }, [sortedData]);

  const handleSearchChange = (event) => {
    const { value } = event.currentTarget;
    setSearch(value);
    const sorted = filterData(data, value);
    setSortedData(sorted);
  };

  const handleActionChange = (value, rowData) => {
    if (value === 'view') {
      setIsUpdate(false);
      setOpen(true);
      setSelected(rowData);
    } else if (value === 'update') {
      setIsUpdate(true);
      setOpen(true);
      setSelected(rowData);
    }
  };

  const renderMobileContracts = () => {
    return sortedData.map((contract, index) => (
      <Paper
        key={index}
        shadow="xs"
        p="md"
        withBorder
        mb="sm"
        className={classes.contractCard}
        style={{ borderRadius: '8px' }}
      >
        <Grid>
          {/* Image and main details */}
          <Grid.Col span={4}>
            <Image
              src={imageUrls[contract.productRefID]}
              height={100}
              fit="cover"
              radius="md"
              alt={contract.productName}
              withPlaceholder
            />
          </Grid.Col>
          
          <Grid.Col span={8}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
              <div>
                <Text weight={600} size="lg" mb={4}>{contract.productName}</Text>
                <Badge color={contract.contractState === 'Active' ? 'green' : 'gray'} mb={5}>
                  {contract.contractState}
                </Badge>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <Dropdown
                  actions={actions}
                  handleActionChange={handleActionChange}
                  data={contract}
                />
              </div>
            </div>
          </Grid.Col>
          
          {/* Details section */}
          <Grid.Col span={12}>
            <Grid mt={5}>
              <Grid.Col span={6}>
                <Text size="sm" color="dimmed">{intl.formatMessage({ id: "product.skuid1" })}</Text>
                <Text>{contract.productSKU}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" color="dimmed">{intl.formatMessage({ id: "product.quantity1" })}</Text>
                <Text>{contract.quantity}</Text>
              </Grid.Col>
              <Grid.Col span={12}>
                <Text size="sm" color="dimmed">{intl.formatMessage({ id: "contract.seller1" })}</Text>
                <Text>{contract.SellerID}</Text>
              </Grid.Col>
              <Grid.Col span={12}>
                <Text size="sm" color="dimmed">{intl.formatMessage({ id: "contract.buyer1" })}</Text>
                <Text>{contract.BuyerID}</Text>
              </Grid.Col>
              <Grid.Col span={12}>
                <Text size="sm" color="dimmed">{intl.formatMessage({ id: "product.price1" })}</Text>
                <Text>{contract.price}</Text>
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>
      </Paper>
    ));
  };

  const renderSearchAndFilter = () => {
    return (
      <Paper className={classes.searchFilterCard} withBorder p="md" style={{ marginBottom: '1rem' }}>
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              placeholder={intl.formatMessage({ id: "myprods.search.placeholder" })}
              value={search}
              onChange={handleSearchChange}
              icon={<IconSearch size={16} />}
            />
          </Grid.Col>
        </Grid>
      </Paper>
    );
  };

  return (
    <>
      <UpdateContractModal 
        open={open} 
        toggle={() => setOpen(false)} 
        isUpdate={isUpdate} 
        selected={selected}
      />
      <ScrollArea className={classes.pageBackground}>
        {renderSearchAndFilter()}
        {sortedData && sortedData.length > 0 
          ? renderMobileContracts() 
          : (
            <Paper p="xl" withBorder style={{ textAlign: 'center' }}>
              <Text size="lg" weight={500} mb="md">
                <FormattedMessage id="contract.nocontract" />
              </Text>
            </Paper>
          )
        }
      </ScrollArea>
    </>
  );
}